import { StandardEvents } from '@shopify/events';

/**
 * YSB (YSB-171): after a successful add (product page, quick add and sticky add to cart) show a
 * "Toegevoegd" notice with the item, "Bekijk winkelwagen", "Verder winkelen" and the free shipping
 * bar, instead of sending the visitor to the cart page (YSB-58). The cart page itself stays.
 * Only shows a notice; never adds anything itself, so add_to_cart tracking is unchanged.
 * Strings and settings come from #ysb-cart-notice-data (snippets/scripts.liquid).
 */

const dataElement = document.getElementById('ysb-cart-notice-data');
const config = dataElement ? JSON.parse(dataElement.textContent || '{}') : {};
const t = config.strings || {};

/** @param {number} cents @param {string} currency */
function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(config.locale || 'nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(
      cents / 100
    );
  } catch {
    return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

/** @returns {HTMLDialogElement} */
function getDialog() {
  let dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('ysb-cart-notice'));
  if (dialog) return dialog;

  dialog = document.createElement('dialog');
  dialog.id = 'ysb-cart-notice';
  dialog.className = 'ysb-cart-notice';
  dialog.setAttribute('aria-labelledby', 'ysb-cart-notice-title');
  document.body.append(dialog);

  dialog.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === dialog || target.closest('[data-ysb-close]')) dialog?.close();
  });

  return dialog;
}

/** @param {any} cart @param {any} item */
function render(cart, item) {
  const dialog = getDialog();
  const threshold = Number(config.threshold) || 0;
  const remaining = threshold - cart.total_price;

  let shipBar = '';
  if (config.showShippingBar && threshold > 0) {
    const progress = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    shipBar =
      remaining > 0
        ? `<div class="ysb-cart-notice__ship" role="status">
            <p>${escapeHtml((t.remaining || '').replace('YSBAMOUNT', formatMoney(remaining, cart.currency)))}</p>
            <div class="ysb-cart-notice__track" aria-hidden="true"><span style="width:${progress}%"></span></div>
          </div>`
        : `<div class="ysb-cart-notice__ship" role="status"><p>${escapeHtml(t.reached)}</p></div>`;
  }

  let image = '';
  if (item?.featured_image?.url) {
    const src = new URL(item.featured_image.url, window.location.origin);
    src.searchParams.set('width', '160');
    image = `<img src="${escapeHtml(src.toString())}" alt="" width="64" height="80" loading="eager">`;
  }
  const variant =
    item && item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="ysb-cart-notice__variant">${escapeHtml(item.variant_title)}</p>`
      : '';

  dialog.innerHTML = `
    <div class="ysb-cart-notice__panel">
      <div class="ysb-cart-notice__head">
        <p class="ysb-cart-notice__title" id="ysb-cart-notice-title">${escapeHtml(t.added)}</p>
        <button type="button" class="ysb-cart-notice__close button-unstyled" data-ysb-close aria-label="${escapeHtml(
          t.close
        )}">&times;</button>
      </div>
      ${
        item
          ? `<div class="ysb-cart-notice__item">
              ${image}
              <div>
                <p class="ysb-cart-notice__name">${escapeHtml(item.product_title)}</p>
                ${variant}
                <p class="ysb-cart-notice__price">${formatMoney(item.final_line_price, cart.currency)}</p>
              </div>
            </div>`
          : ''
      }
      ${shipBar}
      <div class="ysb-cart-notice__actions">
        <a class="button ysb-cart-notice__cart" href="${escapeHtml(config.cartUrl || '/cart')}">${escapeHtml(
          t.viewCart
        )}</a>
        <button type="button" class="button button-secondary ysb-cart-notice__continue" data-ysb-close>${escapeHtml(
          t.continueShopping
        )}</button>
      </div>
    </div>`;

  if (!dialog.open) dialog.showModal();
  dialog.querySelector('.ysb-cart-notice__cart')?.focus();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (Theme.template.name === 'cart') return;

  event.promise
    ?.then(async ({ detail }) => {
      if (detail?.source !== 'product-form-component' || detail.didError) return;

      // The quick add modal closes itself on this event (quick-add.js); the notice opens after the cart fetch
      const response = await fetch(`${config.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const item =
        cart.items.find((/** @type {any} */ line) => String(line.product_id) === String(detail.productId)) ??
        cart.items[0];

      render(cart, item);
    })
    .catch(() => {});
});
